import type { DragEndEvent, DraggableAttributes } from '@dnd-kit/core';
import { closestCenter, DndContext } from '@dnd-kit/core';
import type { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import {
	restrictToParentElement,
	restrictToVerticalAxis,
} from '@dnd-kit/modifiers';
import {
	SortableContext,
	verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { css } from '@emotion/react';
import type { ColumnDef } from '@tanstack/react-table';
import { memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useLocalStorageState from 'use-local-storage-state';

import IconDragVertical from '~icons/carbon/drag-vertical';

import { ButtonCancel, ButtonSave } from '@adflow/frontend/ui-buttons';
import {
	Draggable,
	HubTooltip,
	LineClamp,
	useDndSensors,
} from '@adflow/frontend/ui-common';
import { Checkbox } from '@adflow/frontend/ui-form';
import { LoadingIndicator } from '@adflow/frontend/ui-graphics';
import { ScrollShadowBox } from '@adflow/frontend/ui-layout';
import { Modal } from '@adflow/frontend/ui-modal';
import { su, theme } from '@adflow/frontend/ui-theme';
import { useWindowViewHeight } from '@adflow/frontend/util-common';
import type { DataTableItemType } from '@adflow/shared/util-column-templates';
import { canUserEditColumnTemplate } from '@adflow/shared/util-column-templates';

import { ColumnTemplateDeleteButton } from './ColumnTemplateDeleteButton';
import { ColumnTemplateNameEditor } from './ColumnTemplateNameEditor';
import { SaveColumnTemplateCheckboxes } from './SaveColumnTemplateCheckboxes';
import { columnHeaderHeight, styles } from './styles';
import type { ChangeColumnTemplate, ColumnTemplate } from './types';
import {
	columnDefToStoredSettings,
	isRemoteColumnTemplateId,
	localColumnTemplateId,
} from './types';
import { useCanSaveColumnTemplateForAll } from './useCanSaveColumnTemplateForAll';
import {
	couldColumnBeSticky,
	moveColumn,
	setColumnSticky as setColumnStickyUtil,
	setColumnVisible as setColumnVisibleUtil,
	toggleAllColumnsVisible,
} from './util/column-rules';

interface ColumnTemplateColumnProps<T> {
	columns: ColumnDef<T>[];
	column: ColumnDef<T>;
	index: number;
	setColumnVisible: (
		columnId: string,
		visible: boolean,
		onTooltip: () => void
	) => void;
	setColumnSticky: (
		columnId: string,
		sticky: boolean,
		onTooltip: () => void
	) => void;
	isDragging?: boolean;
	listeners?: SyntheticListenerMap | undefined;
	attributes?: DraggableAttributes;
	canEdit: boolean;
}

function ColumnTemplateColumnImpl<T>({
	columns,
	column,
	index,
	isDragging,
	listeners,
	attributes,
	setColumnVisible,
	setColumnSticky,
	canEdit,
}: ColumnTemplateColumnProps<T>) {
	const { t } = useTranslation('common');
	const [showVisibilityTooltip, setShowVisibilityTooltip] = useState(false);
	const [showStickyTooltip, setShowStickyTooltip] = useState(false);

	const isVisible = column.meta?.visible ?? true;
	const toggleVisible = () => {
		if (canEdit) {
			setColumnVisible(column.id!, !isVisible, () =>
				setShowVisibilityTooltip(true)
			);
		}
	};
	const isSticky = column.meta?.sticky ?? false;

	return (
		<div
			key={column.id}
			css={[styles.bodyRow, isDragging ? styles.bodyRowDragging : null]}
		>
			{listeners && attributes && canEdit ? (
				<div {...listeners} {...attributes} css={styles.dragHandleContainer}>
					<IconDragVertical css={styles.dragHandle} />
				</div>
			) : (
				<div />
			)}

			<div css={styles.checkboxContainer}>
				<HubTooltip
					title={
						showVisibilityTooltip
							? t('columnTemplate.visibleRequirements')
							: null
					}
				>
					<Checkbox
						checked={isVisible}
						onChange={toggleVisible}
						onMouseLeave={() => setShowVisibilityTooltip(false)}
						disabled={!canEdit}
					/>
				</HubTooltip>
			</div>
			<LineClamp lines={1} onClick={toggleVisible}>
				{column.meta?.name}
			</LineClamp>
			<div css={styles.checkboxContainer}>
				{couldColumnBeSticky(columns, index) ? (
					<HubTooltip
						title={
							showStickyTooltip ? t('columnTemplate.stickyRequirements') : null
						}
					>
						<Checkbox
							checked={isSticky}
							onChange={() =>
								setColumnSticky(column.id!, !isSticky, () => {
									setShowStickyTooltip(true);
								})
							}
							onMouseLeave={() => setShowStickyTooltip(false)}
							disabled={!canEdit}
						/>
					</HubTooltip>
				) : null}
			</div>
		</div>
	);
}

const ColumnTemplateColumn = memo(
	ColumnTemplateColumnImpl
) as typeof ColumnTemplateColumnImpl;

interface ColumnTemplateEditorImplProps<T> {
	template?: ColumnTemplate | null;
	columns: ColumnDef<T>[];
	closeColumnTemplateEditor?: () => void;
	minSticky?: number | null;
	changeColumnTemplate?: ChangeColumnTemplate;
	deleteColumnTemplate?: (templateId: number) => void;
	savedColumnTemplatesCount: number;
	savedAllColumnTemplatesCount: number;
	itemType: DataTableItemType;
}

function ColumnTemplateEditorImpl<T>({
	template,
	columns: initialColumns,
	closeColumnTemplateEditor,
	minSticky = null,
	changeColumnTemplate,
	deleteColumnTemplate,
	savedColumnTemplatesCount,
	savedAllColumnTemplatesCount,
	itemType,
}: ColumnTemplateEditorImplProps<T>) {
	const { t } = useTranslation('common');

	const [persistSave, setPersistSave] = useLocalStorageState<boolean>(
		`column-template-${itemType}-save`,
		{ defaultValue: true }
	);

	const initial = {
		name: template?.name ?? t('columnTemplate.defaultName'),

		saved: isRemoteColumnTemplateId(template?.id)
			? true
			: template?.id === localColumnTemplateId
				? false
				: persistSave,

		savedAll: template?.savedAll ?? false,
		savedDefault: template?.savedDefault ?? false,
		columns: initialColumns,
	};

	const [name, setName] = useState<string>(initial.name);
	const [saved, setSaved] = useState<boolean>(initial.saved);
	const [savedAll, setSavedAll] = useState<boolean>(initial.savedAll);
	const [savedDefault, setSavedDefault] = useState<boolean>(
		initial.savedDefault
	);

	const canSaveAll = useCanSaveColumnTemplateForAll();
	const canEdit = canUserEditColumnTemplate({
		canSaveAll,
		savedAll: initial.savedAll,
	});

	const sensors = useDndSensors();

	const [columns, setColumns] = useState(initialColumns);

	const explanation = useMemo(() => {
		if (savedAll && !canSaveAll) {
			if (savedDefault) {
				return t('columnTemplate.saveDefault');
			}

			return null;
		}

		if (!saved) {
			return t('columnTemplate.notSaved');
		}

		if (savedDefault && savedAll) {
			return t('columnTemplate.saveDefaultAll');
		}

		if (savedDefault) {
			return t('columnTemplate.saveDefault');
		}

		if (savedAll) {
			return t('columnTemplate.saveAll');
		}

		if (saved) {
			return t('columnTemplate.save');
		}

		return null;
	}, [savedAll, canSaveAll, saved, savedDefault, t]);

	const setColumnSticky = (
		columnId: string,
		sticky: boolean,
		onTooltip: () => void
	) => {
		setColumns((columns_) =>
			setColumnStickyUtil({
				columns: columns_,
				columnId,
				sticky,
				onLastSticky: onTooltip,
				minSticky,
			})
		);
	};

	const setAllColumnsVisible = () => {
		setColumns((newColumns) => {
			return toggleAllColumnsVisible({
				columns: newColumns,
				minSticky,
			});
		});
	};

	const setColumnVisible = (
		columnId: string,
		visible: boolean,
		onTooltip: () => void
	) => {
		setColumns((columns_) =>
			setColumnVisibleUtil({
				columns: columns_,
				columnId,
				visible,
				onLastVisible: onTooltip,
				minSticky,
			})
		);
	};

	const onDragEnd = (event: DragEndEvent) => {
		setColumns((newColumns) =>
			moveColumn({
				columns: newColumns,
				dragEndEvent: event,
				minSticky,
			})
		);
	};

	const onSubmit = async () => {
		const columnsCopy = [...columns];

		setPersistSave(saved);

		await changeColumnTemplate?.({
			id: template?.id,
			columns: columnsCopy.map(columnDefToStoredSettings),
			name,
			saved,
			savedAll,
			savedDefault,
			columnType: itemType,
		});
	};

	const windowHeight = useWindowViewHeight();

	return (
		<>
			<Modal.Header>
				<h1 css={styles.title}>{t('columnTemplate.selectColumns')}</h1>
			</Modal.Header>
			<ScrollShadowBox
				topOffset={columnHeaderHeight}
				scrollAreaStyle={styles.body(windowHeight - 240)}
			>
				<div css={styles.header}>
					<div />
					<div
						css={
							canEdit
								? {
										justifyContent: 'center',
										cursor: 'pointer',
										color: `${theme.colors.palettes.primary.main} !important`,
										'&:hover': css({ textDecoration: 'underline' }),
									}
								: { justifyContent: 'center' }
						}
						onClick={() => {
							if (!canEdit) return;

							setAllColumnsVisible();
						}}
					>
						{t('show')}
					</div>
					<div>{t('columnTemplate.columnName')}</div>
					<div css={{ justifyContent: 'center' }}>
						{t('columnTemplate.sticky')}
					</div>
				</div>
				<DndContext
					sensors={sensors}
					collisionDetection={closestCenter}
					onDragEnd={onDragEnd}
					modifiers={[restrictToVerticalAxis, restrictToParentElement]}
					autoScroll
				>
					<SortableContext
						items={columns
							.filter((c) => !c.meta?.disableOrdering)
							.map((column) => column.id!)}
						strategy={verticalListSortingStrategy}
					>
						{columns.map((column, index) =>
							column.meta?.disableConfig ? null : column.meta
									?.disableOrdering ? (
								<ColumnTemplateColumn
									key={column.id}
									columns={columns}
									column={column}
									index={index}
									setColumnSticky={setColumnSticky}
									setColumnVisible={setColumnVisible}
									canEdit={canEdit}
								/>
							) : (
								<Draggable key={column.id} id={column.id!} disabled={!canEdit}>
									{(attributes, listeners, isDragging) => (
										<ColumnTemplateColumn
											columns={columns}
											column={column}
											index={index}
											isDragging={isDragging}
											attributes={attributes}
											listeners={listeners}
											setColumnSticky={setColumnSticky}
											setColumnVisible={setColumnVisible}
											canEdit={canEdit}
										/>
									)}
								</Draggable>
							)
						)}
					</SortableContext>
				</DndContext>
			</ScrollShadowBox>
			<div
				css={{
					display: 'flex',
					justifyContent: 'flex-start',
					gap: 10,
					paddingLeft: '1.25rem',
					paddingRight: '1.25rem',
					paddingBottom: '0.5rem',
					paddingTop: '0.5rem',
				}}
			>
				<SaveColumnTemplateCheckboxes
					savedColumnTemplatesCount={savedColumnTemplatesCount}
					savedAllColumnTemplatesCount={savedAllColumnTemplatesCount}
					saved={saved}
					savedAll={savedAll}
					savedDefault={savedDefault}
					setSaved={setSaved}
					setSavedAll={setSavedAll}
					setSavedDefault={setSavedDefault}
					canSaveAll={canSaveAll}
					canEdit={canEdit}
				/>
			</div>
			<div
				css={{
					paddingLeft: '1.25rem',
					paddingRight: '1.25rem',
					marginBottom: '0px',
					marginTop: '1.25rem',
					display: 'flex',
					flexDirection: 'row',
					columnGap: '1.25rem',
				}}
			>
				{!deleteColumnTemplate ||
				!isRemoteColumnTemplateId(template?.id) ||
				!canEdit ? null : (
					<ColumnTemplateDeleteButton
						name={name}
						deleteColumnTemplate={() => deleteColumnTemplate(template.id)}
					/>
				)}
				<div
					css={{
						// Take up as much space as possible
						// but allow the button to shrink
						// for smaller screens
						flexGrow: 1,
						flexShrink: 1,
						minWidth: 0,
					}}
				>
					<ColumnTemplateNameEditor
						name={name}
						setName={setName}
						readOnly={!canEdit}
						disabled={!canEdit || !saved}
					/>
				</div>
				<ButtonCancel
					onClick={() => {
						closeColumnTemplateEditor?.();
					}}
				/>

				<div
					css={{
						display: 'flex',
						justifyContent: 'flex-end',
					}}
				>
					<ButtonSave onClick={onSubmit}>
						{saved ? t('buttons.save') : t('buttons.apply')}
					</ButtonSave>

					{explanation ? (
						<div
							css={[
								su.textXs,
								{
									position: 'absolute',
									color: theme.colors.text.secondary,
									marginTop: '-20px',
									marginRight: 2,

									/**
									 * Make sure text takes up the full line and aligns to the right.
									 * Added specifically for iOS safari. See
									 * https://avbsupport.atlassian.net/browse/HUB-6419
									 */
									width: '100%',
									textAlign: 'right',
								},
							]}
						>
							{explanation}
						</div>
					) : null}
				</div>
			</div>
		</>
	);
}

export interface ColumnTemplateEditorProps<
	T,
> extends ColumnTemplateEditorImplProps<T> {
	isLoading?: boolean;
}

export function ColumnTemplateEditor<T>(props: ColumnTemplateEditorProps<T>) {
	return (
		<Modal
			show
			onRequestClose={() => props.closeColumnTemplateEditor?.()}
			closeOnOverlayClick={false}
			disableFirstElementFocus
			dialogStyleOverrides={[
				{
					overflow: 'unset',
					paddingLeft: '0',
					paddingRight: '0',
					minWidth: '400px',
					maxWidth: '800px',
				},
			]}
		>
			{props.isLoading ? (
				<LoadingIndicator />
			) : (
				<ColumnTemplateEditorImpl
					key={JSON.stringify(props.template)}
					{...props}
				/>
			)}
		</Modal>
	);
}
